//encryption.service

import { Injectable } from '@angular/core';
import { ConfigService } from './configservice.service';

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  private static readonly ALGORITHM = 'AES-CBC';
  private static readonly IV_LENGTH = 16;
  private static readonly BASE64_KEY = '2Vz6iWUrWGrQh0bU9HpmJUDCAWS5y4sXBz+3aJ7lU/o=';
  private static readonly RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAtI1uxROiTBGMQlrLb23s
/3YssgXGSsWi+rLUA5CnElpt7/dks0BFguZw20ak8Z7fxuD/tpuy//aPmCGQzJnC
0Jc4Mv007hD18IKdiVf2LtOhe+edOqQOD2E+kbfCLsOvdYpiNsPk1eyiBylpGSyv
yBur0HjUXzZ72wl4gCMFrfa+YcwVrHx8ekNY/fzr5903bmdMDnCHqAVCC1PmNLHO
yjrUl2Wei4y8WftIK1acF7YkgNJwe8ES2fWe2N/3NRDX3YiRu5FMpqc3/vC+Tc61
itwYoXsCu7fJnbTj5BXjkAIP5V3HjCeFoerDTaWnjq0CLlK3tdTmwoBAmafdV22L
YQIDAQAB-----END PUBLIC KEY-----`.replace(/-----.*-----|\s/g, ''); // Remove headers and whitespace
private aesKey: Uint8Array;

  constructor(private configService: ConfigService) {
    this.aesKey = this.generateAESKey();
  }

  getKrakendUrl(): string {
    const krakendUrl = this.configService.getKrakendUrl();
    if (!krakendUrl) {
      throw new Error("Krakend URL is not available.");
    }
    return krakendUrl;
  }

  // Utility method to generate a random Base64 key
  // private generateBase64Key(): string {
  //   const aesKey = this.generateAESKey(); // Generate a random AES key
  //   return btoa(String.fromCharCode(...aesKey)); // Convert the key to Base64
  // }

  // Utility method to convert ArrayBuffer to a Base64 string
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const byteArray = new Uint8Array(buffer);
    const binaryString = Array.from(byteArray)
        .map((byte) => String.fromCharCode(byte))
        .join('');
    //console.log("AES Binary string: ",binaryString)
    return btoa(binaryString); // Encode binary string to Base64
  }

  private static base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  async base64ToCryptoKey(): Promise<CryptoKey> {
    // console.log(this.base64Key, "this.base64Key");
    
    // const keyBytes = EncryptionService.base64ToUint8Array(this.base64Key);

    return window.crypto.subtle.importKey(
      'raw',
      this.aesKey,
      {
        name: EncryptionService.ALGORITHM,
        length: 256
      },
      true,
      ['encrypt', 'decrypt']
    );
  }

  async encrypt(data: string, key: CryptoKey): Promise<string> {
    const iv = window.crypto.getRandomValues(new Uint8Array(EncryptionService.IV_LENGTH));

    const encoder = new TextEncoder();
    const plaintextBytes = encoder.encode(data);

    const cipher = await window.crypto.subtle.encrypt(
      {
        name: EncryptionService.ALGORITHM,
        iv: iv,
      },
      key,
      plaintextBytes
    );

    // Combine IV and ciphertext
    const combined = new Uint8Array(iv.length + cipher.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(cipher), iv.length);

    return this.arrayBufferToHex(combined.buffer);
  }

  // Utility method to convert ArrayBuffer to a Hexadecimal string
  private arrayBufferToHex(buffer: ArrayBuffer): string {
    const byteArray = new Uint8Array(buffer);
    return Array.from(byteArray)
        .map((byte) => byte.toString(16).padStart(2, '0')) // Convert each byte to 2-digit hex
        .join('');
  }

  async printKeyInHex(key: CryptoKey): Promise<void> {
    const exportedKey = await window.crypto.subtle.exportKey('raw', key);
    const hexKey = this.arrayBufferToHex(exportedKey);
    //console.log(`Key (hexadecimal): ${hexKey}`);
  }

  // New method to encrypt parameters synchronously
  encryptParameters(params: { [key: string]: string }): Promise<{ [key: string]: string }> {
    const krakendUrl = this.configService.getKrakendUrl();
    if (!krakendUrl) {
      // If encryption is disabled, return the plain text parameters
      return Promise.resolve(params);
    }

    return this.base64ToCryptoKey().then((key) => {
      const encryptedParamsPromises = Object.keys(params).map(async (param) => {
        const encryptedValue = await this.encrypt(params[param], key);
        return { [param]: encryptedValue };
      });

      return Promise.all(encryptedParamsPromises).then((encryptedParamsArray) =>
        encryptedParamsArray.reduce((acc, curr) => ({ ...acc, ...curr }), {})
      );
    });
  }

  // Generate a random AES key
  generateAESKey(): Uint8Array {
    // Generate a 256-bit AES key
    // return "13243546576879809483736251234562"
    return window.crypto.getRandomValues(new Uint8Array(32)); // 32 bytes = 256 bits
  }

  base64ToUint8Array(base64: string): Uint8Array {
    // Decode the Base64 string into a binary string
    const binaryString = window.atob(base64);
    const len = binaryString.length;

    // Create a Uint8Array and populate it with the byte values
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes;
  }

  // Encrypt AES key using RSA public key without JSEncrypt
  async encryptAESKeyWithRSA(): Promise<{ hex: string; base64: string; bytes: Uint8Array }> {
    try {
        // Decode the RSA public key
        const publicKeyBytes = EncryptionService.base64ToUint8Array(
            EncryptionService.RSA_PUBLIC_KEY
        );

        // Import the public key
        const publicKey = await window.crypto.subtle.importKey(
            'spki',
            publicKeyBytes,
            {
                name: 'RSA-OAEP',
                hash: { name: 'SHA-256' },
            },
            true,
            ['encrypt']
        );
        //console.log('Public Key Imported:', publicKey);

        // Encrypt the AES key
        const encryptedKey = await window.crypto.subtle.encrypt(
            {
                name: 'RSA-OAEP',
            },
            publicKey,
            this.aesKey
        );
        //console.log('Encrypted Key (ArrayBuffer):', encryptedKey);

        const base64Key = this.arrayBufferToBase64(this.aesKey);
        //console.log('AES Key (BASE64):', base64Key);

        // Convert ArrayBuffer to Uint8Array (byte array)
        const encryptedKeyBytes = new Uint8Array(encryptedKey);
        //console.log('Encrypted Key (Bytes):', encryptedKeyBytes);

        // Convert the byte array to a hexadecimal string
        const encryptedKeyHex = this.arrayBufferToHex(encryptedKey);
        //console.log('Encrypted Key (Hexadecimal):', encryptedKeyHex);

        // Convert the byte array to a Base64 string
        const encryptedKeyBase64 = this.arrayBufferToBase64(encryptedKey);
        //console.log('Encrypted AES Key (Base64):', encryptedKeyBase64);

        // Return all formats
        return {
            hex: encryptedKeyHex,
            base64: encryptedKeyBase64,
            bytes: encryptedKeyBytes,
        };
    } catch (error) {
        console.error('Error during AES Key encryption:', error);
        throw error; // Re-throw for debugging purposes
    }
  }
}